import React, { useState, useEffect } from 'react';
import { X, Download, Upload } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // 检查是否已经安装或是内嵌 WebView
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const isInWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
    const dismissedAt = localStorage.getItem('installPromptDismissed');
    const recentlyDismissed = dismissedAt && Date.now() - parseInt(dismissedAt, 10) < 30 * 24 * 60 * 60 * 1000;

    if (isStandalone || isInWebView || recentlyDismissed) {
      return;
    }

    // iOS Safari（包括 iPad Pro）
    const isIOSSafari = (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && /WebKit/.test(navigator.userAgent) && !/(CriOS|FxiOS|OPiOS|mercury)/.test(navigator.userAgent);
    if (isIOSSafari) {
      setIsIOS(true);
      setShowPrompt(true);
      return;
    }

    // Android / Chrome / Edge
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = () => {
    if (isIOS) {
      // iOS 只显示说明，点击不执行任何操作（用户需要手动分享）
      return;
    }
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    });
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-teal-100 shadow-lg px-4 py-3 safe-area-pb">
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-lg font-bold">P</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">
              {isIOS ? '添加到主屏幕，随时访问 PsychBond' : '添加到桌面，随时访问 PsychBond'}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {isIOS
                ? '点击 Safari 共享按钮，选择「添加到主屏幕」'
                : '安装后无需打开浏览器，像 App 一样使用'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isIOS ? (
            <div className="flex items-center gap-1 text-teal-600 text-sm font-medium px-3 py-1.5 bg-teal-50 rounded-lg">
              <Upload className="w-4 h-4" />
              <span>共享</span>
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="flex items-center gap-1 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>安装</span>
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="关闭"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
